import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';
import { visibilityConfig, type VisibilityConfigDef } from '@alchemiesolns/visibility-config';
import { ArrowDef, type ArrowIdDef } from '@alchemiesolns/arrow-def';
import type { ConfigString } from '@alchemiesolns/mechanism-types';
import { AtomContainer } from '@alchemiesolns/atomcontainer';
import { toAtomContainer, buildIndexMaps, fromAtomContainer } from './atomContainer';
import { resolveAllArrowIds } from './resolve';

/**
 * Serialize a MolecularWorkspace to a config string.
 *
 * The config string format is: a*atoms|b*bonds|p*positions|lp*lonePairs|vis*visibility|ar*arrows|hb*hydrogenBonds
 *
 * Index ordering is NOT canonical — it reflects Map iteration order at the time
 * of serialization. String comparison of config strings is unsupported.
 */
export function toConfigString(workspace: MolecularWorkspace): ConfigString {
  const container = toAtomContainer(workspace);
  const { atomIdToIndex } = buildIndexMaps(workspace);

  // Encode base config (atoms, bonds, positions, lone pairs)
  const baseConfig = container.encode();

  // Build visibility defs using container indices
  // We need to map atom IDs to container array indices (which match Map iteration order)
  const atomIdToContainerIndex = new Map<string, number>();
  container.atoms.forEach((atom: any, index: number) => {
    if (atom.id) {
      atomIdToContainerIndex.set(atom.id, index);
    }
  });

  const previousVisDefs: VisibilityConfigDef[] = [];
  for (const atomId of workspace.getAtomIds()) {
    const flag = workspace.getVisibility(atomId);
    if (flag !== undefined && flag !== VisibilityFlag.Visible) {
      const containerIndex = atomIdToContainerIndex.get(atomId);
      if (containerIndex !== undefined) {
        previousVisDefs.push({ a: containerIndex, visibilityFlag: flag });
      }
    }
  }

  const visDefs = visibilityConfig.extractVisibilityFromContainer(container, previousVisDefs);
  const visConfig = visibilityConfig.encode(visDefs);

  // Encode arrows
  const arrows = workspace.getArrows();
  const arrowsConfig = arrows.length > 0
    ? `|ar*${ArrowDef.encode(resolveAllArrowIds(workspace))}`
    : '';

  // Encode hydrogen bonds
  let hbConfig = '';
  const hydrogenBonds = workspace.getHydrogenBonds();
  if (hydrogenBonds.length > 0) {
    const hbPairs: string[] = [];

    for (const hb of hydrogenBonds) {
      const donorIdx = atomIdToIndex.get(hb.donorAtomId);
      const acceptorIdx = atomIdToIndex.get(hb.acceptorAtomId);
      if (donorIdx !== undefined && acceptorIdx !== undefined) {
        hbPairs.push(`${donorIdx}:${acceptorIdx}`);
      }
    }

    if (hbPairs.length > 0) {
      hbConfig = `|hb*${hbPairs.join(',')}`;
    }
  }

  return (baseConfig + visConfig + arrowsConfig + hbConfig) as ConfigString;
}

/**
 * Parse a legacy config string into a MolecularWorkspace.
 *
 * Handles all config string sections: atoms, bonds, positions, lone pairs,
 * visibility, arrows (ar*), and hydrogen bonds (hb*).
 */
export function fromConfigString(config: string): MolecularWorkspace {
  const container = AtomContainer.decode(config);
  const arrowDefs = ArrowDef.decodeArrows(config);

  // Create workspace from container
  let result = fromAtomContainer(container);

  // The atom/bond ordering in the workspace comes from the container's array order,
  // which matches the config string's index scheme. So we can use getAtomIds()
  // to build the index-to-ID mapping.
  const atomIds = result.getAtomIds();
  const bondIds = result.getBondIds();

  // Apply visibility settings
  const visDefs = visibilityConfig.decode(config);
  for (const visDef of visDefs) {
    const atomId = atomIds[visDef.a];
    if (atomId) {
      result = result.setVisibility(atomId, visDef.visibilityFlag);
    }
  }

  // Parse arrows — convert indices to IDs
  if (arrowDefs.length) {
    const arrowIdDefs: ArrowIdDef[] = [];

    for (const arrow of arrowDefs) {
      const srcIsAtom = arrow.type === 'pa' || arrow.type === 'pb' ||
                        arrow.type === 'ra' || arrow.type === 'rb';
      const dstIsAtom = arrow.type === 'pa' || arrow.type === 'ba' ||
                        arrow.type === 'ra' || arrow.type === 'cba';

      const srcId = srcIsAtom ? atomIds[arrow.src] : bondIds[arrow.src];
      const dstId = dstIsAtom ? atomIds[arrow.dst] : bondIds[arrow.dst];

      if (!srcId || !dstId) continue;

      const idArrow: ArrowIdDef = {
        type: arrow.type,
        srcId,
        dstId,
      };

      if (arrow.p !== undefined) {
        const anchorId = atomIds[arrow.p];
        if (!anchorId) continue;
        idArrow.anchorId = anchorId;
      }

      arrowIdDefs.push(idArrow);
    }

    result = result.setArrows(arrowIdDefs);
  }

  // Parse hydrogen bonds (hb* section)
  const hbMatch = config.match(/\|hb\*([^|]+)/);
  if (hbMatch) {
    const hbPairs = hbMatch[1].split(',');

    for (const pair of hbPairs) {
      const [donorIdxStr, acceptorIdxStr] = pair.split(':');
      const donorIdx = parseInt(donorIdxStr, 10);
      const acceptorIdx = parseInt(acceptorIdxStr, 10);

      if (donorIdx >= 0 && donorIdx < atomIds.length &&
          acceptorIdx >= 0 && acceptorIdx < atomIds.length) {
        const donorAtomId = atomIds[donorIdx];
        const acceptorAtomId = atomIds[acceptorIdx];

        const donorAtom = result.getAtom(donorAtomId);
        if (donorAtom && donorAtom.symbol === 'H') {
          // Find parent atom through bonds
          let donorParentAtomId: string | null = null;
          for (const bond of result.getAllBonds()) {
            if (bond.a1.id === donorAtomId && ['N', 'O', 'F'].includes(bond.a2.symbol)) {
              donorParentAtomId = bond.a2.id;
              break;
            } else if (bond.a2.id === donorAtomId && ['N', 'O', 'F'].includes(bond.a1.symbol)) {
              donorParentAtomId = bond.a1.id;
              break;
            }
          }

          if (donorParentAtomId) {
            result = result.addHydrogenBond({
              donorAtomId,
              donorParentAtomId,
              acceptorAtomId,
            });
          }
        }
      }
    }
  }

  return result;
}
